/* eslint-disable no-nested-ternary */
import { AudioOutlined } from "@ant-design/icons";
import { AccelFile } from "@axl-dev/school-sdk";
import { Button, notification } from "antd";
import { debounce } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";

import { useDevice } from "@app/shared/lib/DeviceProvider";
import { useLoc } from "@app/shared/lib/hooks";
import useAudioRecorder, {
  MediaAudioTrackConstraints,
  useAudioContext,
} from "@app/shared/lib/hooks/useAudioRecorder";
import { TimeSpan } from "@app/shared/lib/TimeSpan";
import { useHandler, withHandler2 } from "@app/shared/lib/with-hander-2";
import { Loc } from "@app/shared/ui/Loc";

import { VoiceRecordButton } from "./components/VoiceRecordButton/VoiceRecordButton";
import { VoiceRecordTouchSlideToCancel } from "./components/VoiceRecordCancel/VoiceRecordCancel";
import { VoiceRecordDuration } from "./components/VoiceRecordDuration/VoiceRecordDuration";
import { VoiceRecordLock } from "./components/VoiceRecordLock/VoiceRecordLock";

import styles from "./VoiceInput.module.scss";

export type VoiceInputHandler = {
  cancelRecording: () => void;
};
type Props = {
  deviceId?: string;
  iconClassname?: string;
  disabled?: boolean;
  onRecordingStarted?: () => void;
  onRecordingEnded: (file: AccelFile) => void;
  onRecordingCanceled?: () => void;
};

export const VoiceInput = withHandler2<Props, VoiceInputHandler>(
  ({
    deviceId,
    handler,
    iconClassname,
    disabled,
    onRecordingStarted,
    onRecordingEnded,
    onRecordingCanceled,
  }) => {
    const loc = useLoc();
    const ref = useRef<HTMLDivElement>(null);
    const [outside, setOutside] = useState(false);
    const [recording, setRecording] = useState(false);
    const [locked, setLocked] = useState(false);
    /**
     * swipe up to lock
     */
    const [dragUpDistance, setDragUpDistance] = useState(0);
    /**
     *  swipe left to cancel (only on touch device)
     */
    const [dragLeftDistance, setDragLeftDistance] = useState(0);

    const [getAudioMeta] = useAudioContext();
    const lockedRef = useRef(false);
    const outsideRef = useRef(false);
    const buttonPressedRef = useRef(false);
    const recordClickedDownRef = useRef(false);
    const recordTimerRef = useRef<number>(null);
    const cancelledRef = useRef(false);
    const recordingRef = useRef(false);
    const elPositionRef = useRef<DOMRect>(null);
    const initialMousePositionRef = useRef<{ x: number; y: number }>({
      x: 0,
      y: 0,
    });

    const audioConstraints = useMemo<MediaAudioTrackConstraints>(
      () => ({ noiseSuppression: true, echoCancellation: true, deviceId }),
      [deviceId],
    );

    const { startRecording, stopRecording, recordingBlob, recordingTime } =
      useAudioRecorder(
        audioConstraints,
        () => {
          notification.error({
            message: loc.word("Comment.voice.micAccessError", {
              default: "Microphone not found or not allowed",
            }),
            duration: 2,
          });
          console.error("Microphone not found or not allowed");
        },
        undefined,
      );

    useHandler(
      handler,
      {
        cancelRecording: () => {
          cancelledRef.current = true;
          stopRecording();
        },
      },
      [stopRecording],
    );

    useEffect(() => {
      if (!recordingBlob) return;
      if (!cancelledRef.current && (lockedRef.current || !outsideRef.current)) {
        (async () => {
          const meta = await getAudioMeta(recordingBlob);
          const ext = meta.formatName?.includes("webm") ? "webm" : "mp4";
          const nativeFile = new File([recordingBlob], `voice.${ext}`, {
            type: meta.formatName,
          });
          onRecordingEnded(
            AccelFile.fromFile(nativeFile, {
              additionalData: meta,
              isVoice: true,
            }),
          );
        })();
      } else {
        onRecordingCanceled?.();
      }

      recordingRef.current = false;
      lockedRef.current = false;
      outsideRef.current = false;
      cancelledRef.current = false;
      setRecording(false);
      setLocked(false);
      setOutside(false);
      setDragUpDistance(0);
      setDragLeftDistance(0);
    }, [recordingBlob, onRecordingCanceled, onRecordingEnded, getAudioMeta]);

    useEffect(() => {
      if (disabled) return;
      const mouseDownHandler = (e: MouseEvent) => {
        recordClickedDownRef.current = true;
        initialMousePositionRef.current = { x: e.clientX, y: e.clientY };
        elPositionRef.current = ref.current!.getBoundingClientRect();

        recordTimerRef.current = setTimeout(async () => {
          if (await startRecording()) {
            onRecordingStarted?.();
            buttonPressedRef.current = true;
            cancelledRef.current = false;
            setDragLeftDistance(0);
            setDragUpDistance(0);
            setRecording(true);
            recordingRef.current = true;

            return;
          }

          console.error(
            "Start recording error. Microphone not found or not allowed.",
          );
        }, 150);
      };

      const rootRef = ref.current;
      rootRef?.addEventListener("pointerdown", mouseDownHandler);

      return () => {
        rootRef?.removeEventListener("pointerdown", mouseDownHandler);
      };
    }, [startRecording, onRecordingStarted, disabled]);

    useEffect(() => {
      const mouseMoveHandler = debounce(
        (e: MouseEvent) => {
          if (!buttonPressedRef.current || lockedRef.current) return;

          const mouseDragDistance =
            initialMousePositionRef.current.y -
            e.clientY -
            (initialMousePositionRef.current.y -
              (elPositionRef.current?.top ?? 0));
          setDragUpDistance(Math.max(0, Math.min(mouseDragDistance, 100)));
          if (mouseDragDistance >= 50) {
            setLocked(true);
            setOutside(true);
            lockedRef.current = true;
            outsideRef.current = true;
          }
        },
        30,
        { leading: true, trailing: true, maxWait: 30 },
      );
      document.addEventListener("mousemove", mouseMoveHandler);

      const mouseLeaveHandler = () => {
        if (!buttonPressedRef.current || lockedRef.current) return;
        setOutside(true);
        outsideRef.current = true;
      };

      const rootRef = ref.current;
      rootRef?.addEventListener("mouseleave", mouseLeaveHandler);

      const mouseEnterHandler = () => {
        if (!buttonPressedRef.current || locked) return;
        setOutside(false);
        outsideRef.current = false;
      };

      rootRef?.addEventListener("mouseenter", mouseEnterHandler);

      return () => {
        document.removeEventListener("mousemove", mouseMoveHandler);
        rootRef?.removeEventListener("mouseleave", mouseLeaveHandler);
        rootRef?.removeEventListener("mouseenter", mouseEnterHandler);
      };
    }, [locked]);

    useEffect(() => {
      const touchMoveHandler = debounce(
        (e: TouchEvent) => {
          //   console.logDev("touch move", e.touches[0]);
          if (!buttonPressedRef.current || lockedRef.current || !e.touches[0])
            return;

          const touchDragUpDistance =
            initialMousePositionRef.current.y -
            e.touches[0].clientY -
            (initialMousePositionRef.current.y -
              (elPositionRef.current?.top ?? 0));
          const touchDragLeftDistance =
            initialMousePositionRef.current.x -
            e.touches[0].clientX -
            (initialMousePositionRef.current.x -
              (elPositionRef.current?.left ?? 0));

          setDragUpDistance(Math.max(0, touchDragUpDistance));
          setDragLeftDistance(Math.max(0, touchDragLeftDistance));
          if (touchDragUpDistance >= 50) {
            setLocked(true);
            setOutside(true);
            lockedRef.current = true;
            outsideRef.current = true;
          }

          if (touchDragLeftDistance >= 100) {
            cancelledRef.current = true;
            stopRecording();
          }
        },
        30,
        { leading: true, trailing: true, maxWait: 30 },
      );
      document.addEventListener("touchmove", touchMoveHandler);

      return () => {
        document.removeEventListener("touchmove", touchMoveHandler);
      };
    }, [stopRecording]);

    useEffect(() => {
      const mouseUpHandler = () => {
        // record btn was not pressed
        if (!recordClickedDownRef.current) return;
        recordClickedDownRef.current = false;

        if (recordTimerRef.current) {
          window.clearTimeout(recordTimerRef.current);
        }

        if (
          !recordingRef.current &&
          !buttonPressedRef.current &&
          !lockedRef.current
        ) {
          // TODO make a notification
          return;
        }

        buttonPressedRef.current = false;
        if (!lockedRef.current) {
          lockedRef.current = false;
          stopRecording();
        }
      };

      document.addEventListener("mouseup", mouseUpHandler);
      document.addEventListener("touchend", mouseUpHandler);

      return () => {
        document.removeEventListener("mouseup", mouseUpHandler);
        document.removeEventListener("touchend", mouseUpHandler);
      };
    }, [stopRecording]);

    const device = useDevice();

    return (
      <div ref={ref} className={styles.voice_input} data-recording={recording}>
        <div className={styles.voice_input_overlay}>
          <VoiceRecordLock
            locked={locked}
            recording={recording}
            percentToLock={
              dragUpDistance === 0 ? 100 : (dragUpDistance / 75) * 100
            }
          />
          <VoiceRecordDuration duration={TimeSpan.fromSeconds(recordingTime)} />
          {locked ? (
            <Button
              className="p-0"
              type="link"
              onClick={() => {
                cancelledRef.current = true;
                stopRecording();
              }}
            >
              <Loc word="Comment.voice.cancel" default="Cancel" />
            </Button>
          ) : device.isTouch ? (
            <VoiceRecordTouchSlideToCancel
              recording={recording}
              locked={locked}
              cancelProgress={dragLeftDistance}
            />
          ) : (
            <Loc
              word="Comment.voice.cancel.help"
              default="Release outside this field to cancel"
              className="text-placeholder"
            />
          )}

          <VoiceRecordButton
            outside={outside}
            locked={locked}
            onStopRecording={stopRecording}
          />
        </div>
        <Button
          type="link"
          icon={<AudioOutlined className={iconClassname} />}
          disabled={disabled}
          size="large"
          className={styles.voice_input_record_btn}
        />
      </div>
    );
  },
);
